import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ResponseModel } from 'src/app/shared/models/ResponseModel';
import { environment } from 'src/environments/environment';

import { INomAuditRequest, INomProcessRequest } from '../interfaces/INomProcessRequest';
import { INomAuditResult } from '../interfaces/INomAuditResult';
import { INomJobStatus } from '../interfaces/INomJobStatus';
import { INomPreNominaFile } from '../interfaces/INomPreNominaFile';
import { INomActivePreNomina } from '../interfaces/INomActivePreNomina';

@Injectable({ providedIn: 'root' })
export class PaysheetProcessService {
    private apiUrl = environment.backUrlApi + 'PaysheetProcess/';

    constructor(private http: HttpClient) {}

    /**
     * Encola el proceso de generación de pre-nómina (tipo Regular, incomeType=1).
     * IMPORTANTE: las fallas de regla de negocio regresan con HTTP 200 y success=false.
     * Para nómina extemporánea usar enqueueExtemporalPreNomina.
     */
    enqueuePreNomina(dto: INomProcessRequest): Observable<ResponseModel<string>> {
        return this.http.post<ResponseModel<string>>(`${this.apiUrl}pre-nomina/enqueue`, dto);
    }

    /**
     * Encola el proceso de generación de pre-nómina extemporánea (incomeType=2).
     * IMPORTANTE: las fallas de regla de negocio regresan con HTTP 200 y success=false.
     */
    enqueueExtemporalPreNomina(dto: INomProcessRequest): Observable<ResponseModel<string>> {
        return this.http.post<ResponseModel<string>>(`${this.apiUrl}extemporal/enqueue`, dto);
    }

    /**
     * Consulta el estado actual del trabajo de pre-nómina en cola.
     * @param dateCut Fecha de corte en formato yyyyMMdd
     */
    getPreNominaStatus(
        nit: string,
        agreement: string,
        dateCut: string
    ): Observable<ResponseModel<INomJobStatus>> {
        return this.http.get<ResponseModel<INomJobStatus>>(
            `${this.apiUrl}pre-nomina/status/${nit}/${agreement}/${dateCut}`
        );
    }

    /**
     * Lista los archivos disponibles para descarga una vez que la pre-nómina fue generada.
     * @param dateCut Fecha de corte en formato yyyyMMdd
     */
    getPreNominaFiles(
        nit: string,
        agreement: string,
        dateCut: string
    ): Observable<ResponseModel<INomPreNominaFile[]>> {
        return this.http.get<ResponseModel<INomPreNominaFile[]>>(
            `${this.apiUrl}pre-nomina/files/${nit}/${agreement}/${dateCut}`
        );
    }

    /**
     * Descarga un archivo de pre-nómina como Blob.
     * @param dateCut Fecha de corte en formato yyyyMMdd
     * @param fileName Nombre del archivo a descargar
     */
    downloadPreNominaFile(
        nit: string,
        agreement: string,
        dateCut: string,
        fileName: string
    ): Observable<Blob> {
        const params = new HttpParams().set('fileName', fileName);
        return this.http.get(
            `${this.apiUrl}pre-nomina/download/${nit}/${agreement}/${dateCut}`,
            { params, responseType: 'blob' }
        );
    }

    /**
     * Genera y descarga el reporte de pre-nómina vía SSRS en el formato indicado.
     * Endpoint: GET PaysheetProcess/pre-nomina/report
     * @param format 'pdf' | 'xls' | 'csv' | 'columnas'
     * @param incomeType 1 = Regular, 2 = Extemporánea
     * @returns Observable<Blob> con el archivo generado por SSRS.
     */
    downloadPreNominaReport(
        nit: string,
        agreement: string,
        dateCut: string,
        format: 'pdf' | 'xls' | 'csv' | 'columnas',
        incomeType: 1 | 2
    ): Observable<Blob> {
        const params = new HttpParams()
            .set('nit', nit)
            .set('agreement', agreement)
            .set('dateCut', dateCut)
            .set('format', format)
            .set('incomeType', incomeType.toString());
        return this.http.get(
            `${this.apiUrl}pre-nomina/report`,
            { params, responseType: 'blob' }
        );
    }

    /**
     * Aprueba la nómina del convenio para el corte indicado.
     * IMPORTANTE: las fallas de regla de negocio regresan con HTTP 200 y success=false.
     */
    approveNomina(dto: INomProcessRequest): Observable<ResponseModel<null>> {
        return this.http.post<ResponseModel<null>>(`${this.apiUrl}approve`, dto);
    }

    /**
     * Ejecuta la auditoría de nómina y devuelve el resultado de inconsistencias.
     * IMPORTANTE: las fallas de regla de negocio regresan con HTTP 200 y success=false.
     */
    repeatAudit(dto: INomAuditRequest): Observable<ResponseModel<INomAuditResult>> {
        return this.http.post<ResponseModel<INomAuditResult>>(`${this.apiUrl}audit/repeat`, dto);
    }

    /**
     * Consulta si hay una pre-nómina activa para el NIT indicado.
     * Usado por el monitor global para polling en background.
     * @param nit NIT de la empresa
     */
    getActivePreNomina(nit: string): Observable<ResponseModel<INomActivePreNomina>> {
        return this.http.get<ResponseModel<INomActivePreNomina>>(
            `${this.apiUrl}active-prenomina/${nit}`
        );
    }
}
